import i18n from 'i18next';
import { action, computed, observable, toJS } from 'mobx';
import queryString from 'query-string';
import api from '../api';
import PaymentMethodEnum from '../enums/PaymentMethodEnum';
import ReceiveMethodEnum from '../enums/ReceiveMethodEnum';
import liff from '@line/liff';

class OrderStore {
  constructor(root) {
    this.root = root;
  }

  // #region ---------------------------------------- Observable ----------------------------------------
  @observable isPrivateDialogOpen = false; // 개인정보 팝업 창 오픈 여부
  @observable disablePaymentButton = false; // 중복 결제 방지를 위한 결제 버튼 비활성화 여부
  @observable orderRequests = ''; // 주문 요청사항
  @observable deliveryRequestsSelect = 'NONE'; // 배달 요청사항 선택
  @observable deliveryRequests = ''; // 배달 요청사항 직접입력
  @observable agree = false; // 개인정보 수집 및 이용 약관 동의 여부
  // 나이스페이/코밴페이 결제 요청 데이터
  @observable paymentData = {
    orderId: '',
    ediDate: '',
    signData: '',
  };
  @observable paymentSBData; // 소프트뱅크페이 결제 요청 데이터
  @observable order; // 주문 데이터
  @observable paymentType = null; // PG사 코드
  @observable paymentMethod = ''; // 결제 방법
  @observable statusCheckInterval; // 주문 상태체크 인터벌
  @observable inputPoint = ''; // 입력 포인트
  @observable liteOrder;
  @observable isLiteOrder = '0';
  // #endregion

  // #region ---------------------------------------- Computed ----------------------------------------
  // 사용 포인트
  @computed get usePoint() {
    return Number(this.inputPoint.toString().replace(/[^0-9]/g, ''));
  }

  // 결제 금액
  @computed get orderAmount() {
    let orderAmount = 0;

    if (this.liteOrder) {
      // ORDER 테이블 총 금액
      orderAmount = this.liteOrder.tamt;
    } else {
      // 장바구니 총 금액
      orderAmount = this.root.cartStore.cartData.menus
        .map(menu => {
          return this.root.menuStore.getMenuAmountInfo(menu, menu.qty).totalAmount;
        })
        .reduce((accumulator, price) => accumulator + price, 0);
    }

    // 배송비 추가
    if (this.root.cartStore.cartData.receiveMethodCode === ReceiveMethodEnum.DELIVERY) {
      orderAmount += Number(this.root.menuStore.storeData.deliveryFee) || 0;
    }

    return orderAmount;
  }

  // 실제 결제 요청금액 (포인트 차감)
  @computed get paymentAmount() {
    return this.orderAmount - this.usePoint;
  }

  // 세금 계산 후 금액
  @computed get paymentAmountInfo() {
    let tax = 0;
    let taxFree = 0;

    if (this.liteOrder) {
      this.liteOrder.menus.forEach(liteMenu => {
        tax += liteMenu.tax;
        taxFree += liteMenu.tax === 0 ? liteMenu.ramt : 0;
      });
    } else {
      this.root.cartStore.cartData.menus.forEach(cartMenu => {
        const menuAmountInfo = this.root.menuStore.getMenuAmountInfo(cartMenu, cartMenu.qty);
        tax += menuAmountInfo.taxAmount;
        taxFree += menuAmountInfo.taxAmount === 0 ? menuAmountInfo.supplyAmount : 0;
      });
    }

    // 포인트 차감 금액만큼 계산
    if (this.usePoint > 0) {
      const rate = this.paymentAmount / this.orderAmount;
      taxFree = Math.round(taxFree * rate);
      tax = Math.round(tax * rate);
    }

    return {
      taxable: this.paymentAmount - taxFree - tax,
      tax,
      taxFree,
    };
  }

  // 상품 수량
  @computed get paymentCount() {
    const menus = this.liteOrder ? this.liteOrder.menus : this.root.cartStore.cartData.menus;
    return menus.reduce((acc, cur) => acc + cur.qty, 0);
  }

  // 결제용 대표 상품명
  @computed get paymentMenuName() {
    let menuName = '';
    const menus = this.liteOrder ? this.liteOrder.menus : this.root.cartStore.cartData.menus;
    const menuCount = this.paymentCount;

    if (menuCount === 1) {
      menuName = menus[0].name;
    } else if (menuCount > 1) {
      menuName = `${menus[0].name}외 ${menuCount - 1}개`;
    }

    return menuName;
  }

  // 결제용 대표 상품코드 (코밴페이)
  @computed get paymentMenuCode() {
    let menuCode = '';
    const menus = this.liteOrder ? this.liteOrder.menus : this.root.cartStore.cartData.menus;
    if (menus.length > 0) {
      menuCode = menus[0].itemCode;
    }
    return menuCode;
  }

  // 결제 코드로 이름 조회
  @computed get paymentMethodName() {
    if (this.order.payments.length > 1) {
      return i18n.t('payment.label.splitPayment');
    } else {
      return PaymentMethodEnum.properties[this.order.payments[0]?.methodCode || PaymentMethodEnum.ETC];
    }
  }

  @computed get orderData() {
    // 일본 업체 구분 코드 조회하여 상세 주소에 추가
    const params = queryString.parse(window.location.search);
    const deliveryCode = params.code || '';

    const data = {
      companyId: this.root.menuStore.storeData.companyId,
      storeCode: this.root.menuStore.storeData.storeCode,
      langCode: i18n.languages[0],
      userId: this.root.userStore.userData.phoneNumber ?? '',
      zipCode: this.root.userStore.userData.zipCode,
      sido: this.root.userStore.userData.sido,
      sigungu: this.root.userStore.userData.sigungu,
      dong: this.root.userStore.userData.dong,
      jibunAddress: this.root.userStore.userData.jibunAddress,
      roadAddress: this.root.userStore.userData.roadAddress,
      addressDetail: deliveryCode
        ? `[${deliveryCode}] ${this.root.userStore.userData.addressDetail}`
        : this.root.userStore.userData.addressDetail,
      latitude: this.root.userStore.userData.latitude,
      longitude: this.root.userStore.userData.longitude,
      requests:
        this.root.cartStore.cartData.receiveMethodCode === ReceiveMethodEnum.DELIVERY
          ? this.deliveryRequests
          : this.orderRequests,
      channelCode: 1,
      receiveMethodCode: this.root.cartStore.cartData.receiveMethodCode,
      point: this.usePoint,
      paymentType: this.paymentType,
      paymentMethod: this.paymentMethod,
      paymentAmount: this.paymentAmount,
      paymentTaxable: this.paymentAmountInfo.taxable,
      paymentTax: this.paymentAmountInfo.tax,
      paymentTaxFree: this.paymentAmountInfo.taxFree,
      isPointAdded: this.root.userStore.userData.isPointAdded,
      lineLiffAccessToken: liff.isInClient() ? liff.getAccessToken() : '',
      extOrderNo: this.liteOrder ? this.liteOrder.receiptNumber : null,
      deliveryCode: deliveryCode,
    };

    if (this.liteOrder) {
      data.menus = this.liteOrder.menus;
    } else {
      data.menus = this.root.cartStore.cartData.menus.map(cartMenu => {
        return {
          storeCode: cartMenu.storeCode,
          groupCode: cartMenu.groupCode,
          itemCode: cartMenu.itemCode,
          qty: cartMenu.qty,
          price: cartMenu.price,
          couponID: cartMenu.couponData?.CouponID,
          toppingGroups: toJS(cartMenu.toppingGroups),
          setMenuGroups: toJS(cartMenu.setMenuGroups),
        };
      });
    }
    return data;
  }
  // #endregion

  // #region ---------------------------------------- Action ----------------------------------------
  @action
  setInputPoint(value) {
    // 입력한 포인트를 초기값으로 설정
    let inputPoint = Number(value.toString().replace(/[^0-9]/g, ''));

    // 최대 포인트 사용 액수를 주문 금액으로 제한
    if (inputPoint > this.orderAmount) inputPoint = this.orderAmount;

    // 최대 포인트 사용 액수를 보유 포인트로 제한
    const userPoint = this.root.userStore.userPoint;
    if (inputPoint > userPoint) inputPoint = userPoint;

    this.inputPoint = inputPoint.toLocaleString();
  }

  // 개인정보 수집 및 이용 동의 다이얼로그 오픈 여부 상태 값 변경
  @action
  setPrivateDialogOpen(isPrivateDialogOpen) {
    this.isPrivateDialogOpen = isPrivateDialogOpen;
  }

  @action
  setDisablePaymentButton(value) {
    this.disablePaymentButton = value;
  }

  // 결제 요청 데이터 세팅
  @action
  setPaymentData(data) {
    this.paymentData = {
      orderId: data.orderId,
      ediDate: data.ediDate,
      ediTime: data.ediTime,
      signData: data.signData,
    };
  }

  @action
  setPaymentType(value) {
    this.paymentType = value;
  }

  @action
  setPaymentMethod(value) {
    this.paymentMethod = value;
  }

  // 소프트뱅크페이 결제 요청 데이터 세팅
  @action
  setPaymentSBData(data) {
    this.paymentSBData = data;
  }

  /**
   * 주문 조회
   * @param {string} orderId 주문 아이디
   */
  @action
  setOrder(data) {
    this.order = data;
  }

  /**
   * 주문 상태 갱신
   * @param {string} orderId 주문 아이디
   */
  @action
  async setOrderStatus(companyId, storeCode, orderId) {
    if (this.root.commonStore.error === null) {
      const response = await api.getOrderState(companyId, storeCode, orderId);

      // 주문 상태 변경
      this.order.currentStatusCode = Number(response.data);
    }
  }

  @action
  setStatusCheckInterval(interval) {
    this.statusCheckInterval = interval;
  }

  @action
  async setLiteOrder(orderId) {
    try {
      const response = await api.getLiteOrder(
        this.root.commonStore.companyId,
        this.root.commonStore.storeCode,
        orderId,
        i18n.languages[0],
      );
      if (!response.data) throw new Error(i18n.t('order.error.notFound'));

      this.liteOrder = response.data;
      this.isLiteOrder = '1';
      if ((this.root.menuStore.storeData.orderType & 2) === 2) {
        this.root.cartStore.setReceiveMethod(ReceiveMethodEnum.TAKEOUT);
      } else if ((this.root.menuStore.storeData.orderType & 4) === 4) {
        this.root.cartStore.setReceiveMethod(ReceiveMethodEnum.DELIVERY);
      }
    } catch (error) {
      this.root.commonStore.setError(error);
      throw error;
    }
  }
  // #endregion

  // #region ---------------------------------------- 일반 함수 ----------------------------------------
  // #edregion
}

export default OrderStore;
